import {
  backButton,
  hapticFeedback,
  init,
  isTMA,
  mainButton,
  miniApp,
  mockTelegramEnv,
  retrieveRawInitData,
  themeParams,
  viewport,
} from '@telegram-apps/sdk-react';

/**
 * Единственное место, где приложение разговаривает с Telegram SDK.
 *
 * Всё обёрнуто в try/catch осознанно: набор доступных методов зависит от
 * версии клиента и платформы. Нативная кнопка, которой нет в старом Telegram,
 * не должна ронять приложение — она просто не появится.
 */

export type Platform = 'telegram' | 'browser';

export interface TelegramBridge {
  platform: Platform;
  /** сырая строка initData: ею gateway подтверждает, кто мы */
  initDataRaw: string | null;
  isDark: boolean;
}

/** `backgroundColor` → `--tg-background-color` */
function cssVarName(key: string): string {
  return `--tg-${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
}

function quiet(action: () => unknown): void {
  try {
    action();
  } catch {
    // Метод не поддержан этим клиентом — работаем без него.
  }
}

/** Подделка окружения для разработки в обычном браузере. */
function mockForDevelopment(): void {
  const user = encodeURIComponent(
    JSON.stringify({
      id: 0,
      first_name: 'Локальна',
      last_name: 'Розробка',
      username: 'dev',
      language_code: 'uk',
    }),
  );
  const theme = encodeURIComponent(
    JSON.stringify({
      bg_color: '#ffffff',
      text_color: '#0d1526',
      hint_color: '#707a8c',
      button_color: '#2a7cf7',
      button_text_color: '#ffffff',
      secondary_bg_color: '#f2f3f7',
    }),
  );

  mockTelegramEnv({
    launchParams:
      `tgWebAppData=user=${user}&auth_date=${Math.floor(Date.now() / 1000)}&hash=dev` +
      `&tgWebAppVersion=8.0&tgWebAppPlatform=tdesktop&tgWebAppThemeParams=${theme}`,
  });
}

let bridge: TelegramBridge | null = null;

/**
 * Поднять мост до Telegram. Вызывается один раз до рендера, потому что от
 * результата зависит и тема, и то, сможем ли мы вообще авторизоваться.
 */
export function initTelegram(): TelegramBridge {
  if (bridge) return bridge;

  let inTelegram = false;
  try {
    inTelegram = isTMA();
  } catch {
    inTelegram = false;
  }

  if (!inTelegram && import.meta.env.DEV) {
    quiet(mockForDevelopment);
    try {
      inTelegram = isTMA();
    } catch {
      inTelegram = false;
    }
  }

  if (!inTelegram) {
    bridge = { platform: 'browser', initDataRaw: null, isDark: prefersDark() };
    return bridge;
  }

  quiet(() => init());
  quiet(() => miniApp.mountSync());
  quiet(() => themeParams.mountSync());
  // Цвета темы становятся CSS-переменными: дальше всё решает CSS, без ре-рендеров.
  quiet(() => themeParams.bindCssVars(cssVarName));
  quiet(() => backButton.mount());
  quiet(() => mainButton.mount());
  quiet(() => {
    void viewport.mount().then(() => {
      quiet(() => viewport.expand());
    });
  });
  quiet(() => miniApp.ready());

  let initDataRaw: string | null = null;
  try {
    initDataRaw = retrieveRawInitData() ?? null;
  } catch {
    initDataRaw = null;
  }

  bridge = {
    platform: 'telegram',
    initDataRaw,
    isDark: readIsDark(),
  };
  return bridge;
}

export function prefersDark(): boolean {
  return typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export function readIsDark(): boolean {
  try {
    if (themeParams.isMounted()) return themeParams.isDark();
  } catch {
    // ниже — запасной вариант
  }
  try {
    if (miniApp.isMounted()) return miniApp.isDark();
  } catch {
    // ниже — запасной вариант
  }
  return prefersDark();
}

/** Подписка на смену темы в клиенте Telegram. */
export function onThemeChange(listener: (isDark: boolean) => void): () => void {
  const unsubscribers: Array<() => void> = [];

  try {
    unsubscribers.push(themeParams.state.sub(() => listener(readIsDark())));
  } catch {
    // SDK не смонтирован — слушаем системную тему.
  }

  if (typeof window !== 'undefined') {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (): void => listener(readIsDark());
    media.addEventListener('change', handler);
    unsubscribers.push(() => media.removeEventListener('change', handler));
  }

  return () => unsubscribers.forEach((off) => off());
}

/** Нативная кнопка «назад» в шапке Telegram. */
export const nativeBackButton = {
  show(onClick: () => void): () => void {
    let offClick: (() => void) | null = null;
    quiet(() => {
      backButton.show();
      offClick = backButton.onClick(onClick);
    });
    return () => {
      offClick?.();
      quiet(() => backButton.hide());
    };
  },
};

export interface MainButtonParams {
  text: string;
  onClick: () => void;
  loading?: boolean;
  enabled?: boolean;
}

/** Нативная главная кнопка: выглядит родной и не занимает место в вёрстке. */
export const nativeMainButton = {
  show(params: MainButtonParams): () => void {
    let offClick: (() => void) | null = null;
    quiet(() => {
      mainButton.setParams({
        text: params.text,
        isVisible: true,
        isEnabled: params.enabled !== false,
        isLoaderVisible: params.loading === true,
      });
      offClick = mainButton.onClick(params.onClick);
    });
    return () => {
      offClick?.();
      quiet(() => mainButton.setParams({ isVisible: false, isLoaderVisible: false }));
    };
  },
};

/**
 * Хаптика. Дёшево и сильно влияет на ощущение «нативности»: лёгкий отклик
 * на нажатие и чёткий на успех или ошибку.
 */
export const haptics = {
  tap(): void {
    quiet(() => hapticFeedback.impactOccurred('light'));
  },
  press(): void {
    quiet(() => hapticFeedback.impactOccurred('medium'));
  },
  success(): void {
    quiet(() => hapticFeedback.notificationOccurred('success'));
  },
  warning(): void {
    quiet(() => hapticFeedback.notificationOccurred('warning'));
  },
  error(): void {
    quiet(() => hapticFeedback.notificationOccurred('error'));
  },
  select(): void {
    quiet(() => hapticFeedback.selectionChanged());
  },
};

export function closeApp(): void {
  quiet(() => miniApp.close());
}

export type HomeScreenStatus = 'unsupported' | 'unknown' | 'added' | 'missed';

/**
 * Проверить статус ярлыка на главном экране (Bot API 8.0+).
 * Возвращает: 'unsupported' | 'unknown' | 'added' | 'missed'
 */
export function checkHomeScreenStatus(callback: (status: HomeScreenStatus) => void): void {
  try {
    const webApp = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : undefined;
    if (webApp && typeof webApp.checkHomeScreenStatus === 'function') {
      webApp.checkHomeScreenStatus(callback);
    } else {
      callback('unsupported');
    }
  } catch {
    callback('unsupported');
  }
}

/**
 * Вызвать нативное окно добавления ярлыка на главный экран смартфона (Bot API 8.0+).
 */
export function addToHomeScreen(): boolean {
  try {
    const webApp = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : undefined;
    if (webApp && typeof webApp.addToHomeScreen === 'function') {
      webApp.addToHomeScreen();
      return true;
    }
  } catch {
    // игнорируем ошибку неподдерживаемого клиента
  }
  return false;
}

